import * as THREE from 'three';

import { STRINGS } from './strings.zh-HK';
import { GameState } from '../game/GameState';

export interface DisplayData {
  state: GameState;
  /** Whole seconds left on the countdown, or null when not counting down. */
  countdown: number | null;
  /** Simulated seconds since the ball was released, or null before release. */
  elapsed: number | null;
  /** Points awarded this round. */
  score: number;
  /** Points across every round since boot. */
  totalScore: number;
  /** Pocket label once the round resolved, or null while it is live. */
  slotLabel: string | null;
  /** Round number, counting from 1. */
  round: number;
}

/**
 * ============================================================================
 * THE MARQUEE - the machine's own display, at the top of the cabinet
 * ============================================================================
 *
 * This is the panel the player actually reads during a round: the countdown they
 * are aiming against, the points this round, and the running total. It is one of
 * the three parts of the machine the design asks to be visible at once.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS DOM AND NOT AN IN-SCENE TEXTURE
 * ---------------------------------------------------------------------------
 * Text on a texture is blurred at any distance, needs a canvas redrawn every
 * frame, and cannot be read by a test. A DOM element is crisp at every pixel
 * ratio, is selectable, is announced by assistive technology, and - the reason
 * that decided it - `verify-integration.mjs` can assert on its `textContent`.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS ANCHORED TO A MODEL NODE
 * ---------------------------------------------------------------------------
 * The panel is positioned by PROJECTING the cabinet's own `cabinet_led_panel`
 * node onto the screen. It is not absolutely positioned at a hand-tuned offset,
 * so it sits on the modelled bezel at every viewport size, follows the camera
 * when the cabinet is reframed, and moves with the marquee if the cabinet is
 * rebuilt. `App` hands the anchor in; this class never guesses where the display
 * lives on the machine.
 *
 * The panel is sized from the PROJECTED WIDTH of that node, so it scales with the
 * cabinet instead of staying a fixed pixel size while everything else zooms.
 */
export class Display {
  private readonly root: HTMLDivElement;
  private readonly countdownLabel: HTMLElement;
  private readonly countdownValue: HTMLElement;
  private readonly roundValue: HTMLElement;
  private readonly totalValue: HTMLElement;

  /** Scratch vector, reused so `place()` allocates nothing per frame. */
  private readonly projected = new THREE.Vector3();
  private readonly edge = new THREE.Vector3();

  /**
   * The panel's natural height, in `em`.
   *
   * The sum of what `.marquee` and its children actually add up to: 0.56em of
   * padding, a 0.44em label, a 0.18em gap and a 1.05em value. Kept here rather
   * than measured so `place()` stays allocation-free and does not force a layout
   * every frame; `verify-integration.mjs` asserts the panel really does fit the
   * bezel, which is what catches this drifting out of step with the CSS.
   */
  private static readonly EM_HEIGHT = 2.3;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'marquee';
    this.root.id = 'marquee';

    const timer = Display.addField(this.root, STRINGS.display.countdown, STRINGS.display.idle);
    this.countdownLabel = timer.label;
    this.countdownValue = timer.value;

    const round = Display.addField(this.root, STRINGS.display.round, '0');
    this.roundValue = round.value;

    const total = Display.addField(this.root, STRINGS.display.total, '0');
    this.totalValue = total.value;

    parent.appendChild(this.root);
  }

  private static addField(
    parent: HTMLElement,
    label: string,
    initial: string,
  ): { label: HTMLElement; value: HTMLElement } {
    const field = document.createElement('div');
    field.className = 'marquee-field';

    const labelEl = document.createElement('div');
    labelEl.className = 'marquee-label';
    labelEl.textContent = label;

    const valueEl = document.createElement('div');
    valueEl.className = 'marquee-value';
    valueEl.textContent = initial;

    field.append(labelEl, valueEl);
    parent.appendChild(field);
    return { label: labelEl, value: valueEl };
  }

  /**
   * Sit the panel on the cabinet's LED panel.
   *
   * `anchor` is the world-space CENTRE of the LED panel and `halfWidth` /
   * `halfHeight` its half-extents in metres; all three are measured from the
   * loaded cabinet, so nothing here is a hand-tuned constant.
   *
   * The FONT SIZE is derived from the panel's projected HEIGHT rather than its
   * width, which is what makes the readout fit the machine's screen. Sizing from
   * the width looks right until the anchor is a wide, short strip - which is
   * exactly what an LED marquee is - and then two lines of text overflow the
   * bezel and get clipped by the cabinet above them. That is what the first
   * smoke screenshot showed.
   */
  place(
    camera: THREE.Camera,
    width: number,
    height: number,
    anchor: THREE.Vector3,
    halfWidth: number,
    halfHeight: number,
  ): void {
    this.projected.copy(anchor).project(camera);
    this.edge.set(anchor.x + halfWidth, anchor.y, anchor.z).project(camera);

    const screenX = (this.projected.x * 0.5 + 0.5) * width;
    const screenY = (-this.projected.y * 0.5 + 0.5) * height;
    // Pixels per metre on the board's plane, measured along screen X.
    const pixelsPerMetre = (Math.abs(this.edge.x - this.projected.x) * width) / Math.max(halfWidth, 1e-6);

    this.root.style.left = `${screenX}px`;
    this.root.style.top = `${screenY}px`;
    this.root.style.width = `${2 * halfWidth * pixelsPerMetre}px`;
    this.root.style.fontSize = `${Math.max(5, (2 * halfHeight * pixelsPerMetre) / Display.EM_HEIGHT)}px`;
  }

  update(data: DisplayData): void {
    if (data.countdown !== null) {
      this.countdownLabel.textContent = STRINGS.display.countdown;
      this.countdownValue.textContent = String(Math.max(1, Math.ceil(data.countdown)));
    } else if (data.elapsed !== null && data.state === GameState.Playing) {
      this.countdownLabel.textContent = STRINGS.display.timer;
      this.countdownValue.textContent = `${data.elapsed.toFixed(1)}`;
    } else {
      this.countdownLabel.textContent = STRINGS.display.timer;
      this.countdownValue.textContent = STRINGS.display.idle;
    }

    this.roundValue.textContent = String(data.score);
    this.totalValue.textContent = String(data.totalScore);
  }

  /**
   * What the panel is currently showing.
   *
   * Exposed so `verify-integration.mjs` can assert on the display the player
   * reads, rather than only on the numbers behind it. A display wired to the
   * wrong field would look perfectly healthy from the inside.
   */
  get timerText(): string {
    return this.countdownValue.textContent ?? '';
  }

  get roundText(): string {
    return this.roundValue.textContent ?? '';
  }

  get totalText(): string {
    return this.totalValue.textContent ?? '';
  }
}
